import { Stamp } from './Stamp.js';

interface Feature {
  badge: string;
  badgeVariant?: 'default' | 'positive' | 'suggestion' | 'critical';
  title: string;
  body: string;
  accent: string;
}

const FEATURES: ReadonlyArray<Feature> = [
  {
    badge: 'diagnóstico',
    badgeVariant: 'suggestion',
    title: 'Pontos críticos com motivo fundamentado',
    body: 'Cada problema vem com a parte do seu perfil que o gerou, a explicação de por que recrutadores ou o algoritmo vão te penalizar por isso, e uma proposta concreta de como arrumar. Você sai sabendo o que mexer e por quê.',
    accent: 'var(--accent-saffron)',
  },
  {
    badge: 'reescrita',
    badgeVariant: 'positive',
    title: 'Headline e summary refeitos',
    body: 'A IA reescreve em duas ou três opções, sempre respeitando o que você de fato faz. Você vê o racional de cada versão e escolhe a que mais combina com a vaga que está mirando.',
    accent: 'var(--accent-forest)',
  },
  {
    badge: 'posts',
    badgeVariant: 'suggestion',
    title: 'Rascunhos prontos no seu estilo',
    body: 'A partir de um post do LinkedIn (texto e imagem), de um assunto em alta ou de um texto que você cole, saem três a cinco rascunhos com ganchos variados, prontos pra você copiar e ajustar antes de publicar.',
    accent: 'var(--accent-saffron)',
  },
  {
    badge: 'cadência',
    badgeVariant: 'default',
    title: 'Painel da sua presença',
    body: 'Histórico de análises com comparativo entre versões, meta semanal definida por você, e gráfico mostrando como seus pontos críticos diminuem ao longo do tempo. Você decide o ritmo, a gente só registra.',
    accent: 'var(--ink-secondary)',
  },
  {
    badge: 'privacidade',
    badgeVariant: 'positive',
    title: 'Roda local, com sua chave',
    body: 'PDF é processado em memória e descartado quando a sessão expira. A chave da IA vive só na sessão. Salvar análises é opt-in, e ninguém além de você consegue acessá-las.',
    accent: 'var(--accent-forest)',
  },
  {
    badge: 'segurança',
    badgeVariant: 'critical',
    title: 'Login bem feito',
    body: 'Senha em Argon2id (estado da arte em 2026, não bcrypt). 2FA opcional com códigos de recuperação. Proteção contra força bruta, CSRF e replay de código de autenticador. O básico, mas bem feito.',
    accent: 'var(--accent-crimson)',
  },
];

export function Features() {
  return (
    <section id="features" className="border-b border-[var(--border-subtle)] py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-6">
        <header className="max-w-2xl">
          <Stamp variant="default">funcionalidades</Stamp>
          <h2 className="mt-4 font-serif text-[36px] font-medium leading-[1.1] tracking-[-0.02em] text-[var(--ink-primary)] sm:text-[44px]">
            O que faz<span className="text-[var(--accent-saffron)]">.</span>
          </h2>
          <p className="mt-3 font-sans text-base text-[var(--ink-secondary)]">
            Lista honesta. Tudo abaixo está implementado e em uso agora. O que ainda não foi
            construído fica logo abaixo, no roadmap aberto, onde você pode votar pra acelerar.
          </p>
        </header>

        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <article
              key={f.title}
              className="card-paper relative rounded-sm px-6 py-5"
              style={{ borderLeft: `3px solid ${f.accent}` }}
            >
              <Stamp variant={f.badgeVariant ?? 'default'}>{f.badge}</Stamp>
              <h3 className="mt-3 font-serif text-lg font-medium text-[var(--ink-primary)]">
                {f.title}
              </h3>
              <p className="mt-2 font-sans text-sm leading-relaxed text-[var(--ink-secondary)]">
                {f.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
